import type { GalleryPhoto, Photo } from '@capacitor/camera'
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera'
import { CapacitorException } from '@capacitor/core'
import { Group, Image, Stack } from '@mantine/core'
import { AboveAllModal } from 'components/AboveAllModalOverlay/AboveAllModalOverlay'
import { Button } from 'components/Button/Button'
import { IconCamera } from 'components/icons/IconCamera'
import { translate } from 'i18n/i18n'
import React from 'react'
import { captureFrontendError } from 'utils/captureFrontendError'
import { createErrorModal } from '../utils/createCommonModals'

type ImageUploadProps = {
  onlyCamera?: boolean
  image?: Photo | GalleryPhoto
  setImage: (image: Photo | GalleryPhoto) => void
  fullScreen?: boolean
}

const handlePermissionsError = (error: unknown) => {
  if (error instanceof CapacitorException) {
    if (error.message.match('denied')) {
      //permission denied
      AboveAllModal.open(
        createErrorModal(translate.phrases.placeholder('Permission required'), (error as Error).message),
      )

      return
    }

    if (error.message.match('cancelled')) {
      //cancelled
      return
    }
  }

  captureFrontendError(error)

  throw error
}

export const ImageUpload: React.FC<ImageUploadProps> = ({ onlyCamera, image, setImage, fullScreen }) => {
  const [failedSrc, setFailedSrc] = React.useState<string>()

  const handleTakePhoto = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        source: CameraSource.Camera,
        resultType: CameraResultType.DataUrl,
      })

      setImage(image)
    } catch (error) {
      handlePermissionsError(error)
    }
  }

  const handleSelectPhoto = async () => {
    try {
      const { photos } = await Camera.pickImages({ limit: 1 })

      setImage(photos[0])
    } catch (error) {
      handlePermissionsError(error)
    }
  }

  const imageSrc = (image as Photo)?.dataUrl || (image as GalleryPhoto)?.webPath
  const isFullScreen = fullScreen || true

  return (
    <Group justify="center" grow>
      <Stack>
        {imageSrc && failedSrc !== imageSrc ? (
          <Image
            src={imageSrc}
            h={isFullScreen ? '60vh' : undefined}
            w={'100%'}
            fit={'contain'}
            onError={() => setFailedSrc(imageSrc)}
          />
        ) : (
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: isFullScreen ? '60vh' : undefined,
              width: '100%',
            }}
          >
            <span style={{ fontSize: 160 }}>
              <IconCamera />
            </span>
          </div>
        )}
        <Group gap="xs" justify="center">
          <Button variant="primary" onClick={() => handleTakePhoto()}>
            {translate.phrases.placeholder('Use Camera')}
          </Button>
          {!(onlyCamera === true) && (
            <Button variant="secondary" onClick={() => handleSelectPhoto()}>
              {translate.phrases.placeholder('Select Photo')}
            </Button>
          )}
        </Group>
      </Stack>
    </Group>
  )
}
